
import { useState, useEffect, useCallback } from 'react';
import { getTeacherById, deleteTeacher as deleteTeacherService, saveTeacher } from '@/services/db/teachers';
import { Teacher } from '@/types/teacher.types';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export const useTeacherDetails = (teacherId?: string) => {
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const navigate = useNavigate();
  
  const fetchTeacher = useCallback(async () => {
    if (!teacherId) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Get teacher from database
      const foundTeacher = await getTeacherById(teacherId);
      
      if (foundTeacher) {
        setTeacher(foundTeacher);
      } else {
        setTeacher(null);
        toast.error("Teacher not found");
      }
    } catch (error) {
      console.error("Error fetching teacher details:", error);
      setError(error instanceof Error ? error : new Error("Unknown error fetching teacher"));
      toast.error("Failed to load teacher details. Please try again.");
      setTeacher(null);
    } finally {
      setIsLoading(false);
    }
  }, [teacherId]);
  
  // Fetch teacher on component mount or when teacherId changes
  useEffect(() => {
    fetchTeacher();
  }, [fetchTeacher]);
  
  const deleteTeacher = async () => {
    if (!teacherId) {
      toast.error("Cannot delete: Teacher ID is missing");
      return Promise.reject(new Error("Teacher ID is required"));
    }
    
    setIsLoading(true);
    
    try {
      await deleteTeacherService(teacherId);
      toast.success("Teacher deleted successfully");
      navigate('/teachers');
      return true;
    } catch (error) {
      console.error("Error deleting teacher:", error);
      toast.error("Failed to delete teacher. Please try again.");
      return Promise.reject(error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const updateTeacher = async (updatedTeacher: Teacher) => {
    if (!updatedTeacher.id) {
      toast.error("Cannot update: Teacher ID is missing");
      return Promise.reject(new Error("Teacher ID is required"));
    }
    
    setIsLoading(true);
    try {
      const result = await saveTeacher(updatedTeacher);
      setTeacher(result);
      toast.success("Teacher information updated successfully");
      return result;
    } catch (error) {
      console.error("Error updating teacher:", error);
      toast.error("Failed to update teacher information. Please try again.");
      return Promise.reject(error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const refreshTeacher = fetchTeacher;
  
  return {
    teacher,
    isLoading,
    error,
    deleteTeacher,
    updateTeacher,
    refreshTeacher
  };
};
